/**
 * ScoringWorkspace — three-column container for the Scoring tab.
 *
 *   ┌─────────────────┬─────────────────┬─────────────────┐
 *   │  SCORE ENTRY    │    RANKING      │    CRITERIA     │
 *   │  (criteria ×    ├─────────────────┤                 │
 *   │   sites matrix) │  SCORE PROFILE  │   col-span-3    │
 *   │   col-span-5    │   col-span-4    │                 │
 *   └─────────────────┴─────────────────┴─────────────────┘
 *
 * Mobile (<1024px): everything stacks vertically — SCORE ENTRY → RANKING →
 * SCORE PROFILE → CRITERIA.
 *
 * Modals (CriterionForm + TemplateModal) are owned here so the panel
 * components can stay stateless wrt dialog open/close.
 *
 * Linear: MYD-207 (E2). Supersedes the cancelled MYD-136.
 */

import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { BarChart3, Trophy } from 'lucide-react'
import { Card, CardContent } from 'components/ui/card'

import { fetchCriteria, fetchScoringSummary } from 'slices/scoringSlice'
import { fetchSites } from 'slices/evaluationSiteSlice'

import CriterionForm from '../CriterionForm'
import ApplyTemplateModalCN from '../templates/ApplyTemplateModalCN'
import SaveAsTemplateModalCN from '../templates/SaveAsTemplateModalCN'

import CriteriaPanel from './CriteriaPanel'
import ScoreEntrySection from './ScoreEntrySection'
import RankingCard from './RankingCard'
import ScoreProfileCard from './ScoreProfileCard'

// Column spans out of 12 — the main dials for tuning this layout.
const MATRIX_SPAN = 'lg:col-span-5'
const SIDEBAR_SPAN = 'lg:col-span-4'
const CRITERIA_SPAN = 'lg:col-span-3'

// Ranking shows this many sites, then scrolls. Keeps the sidebar column short
// enough that Score Profile stays visible without scrolling the page.
const RANKING_VISIBLE_ROWS = 3

interface ScoringWorkspaceProps {
	evalId: string
}

const ScoringWorkspace = ({ evalId }: ScoringWorkspaceProps) => {
	const dispatch = useDispatch()

	const [formOpen, setFormOpen] = useState(false)
	const [editingCriterion, setEditingCriterion] = useState<any>(null)
	const [applyOpen, setApplyOpen] = useState(false)
	const [saveAsOpen, setSaveAsOpen] = useState(false)

	const summary = useSelector((state: any) => state.scoring?.summaryByEvaluation?.[evalId])
	const criteriaCount = useSelector((state: any) => (state.scoring?.criteriaByEvaluation?.[evalId] || []).length)
	const evalCriteria = useSelector((state: any) =>
		(state.scoring?.criteriaByEvaluation?.[evalId] || [])
			.slice()
			.sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
	)

	useEffect(() => {
		// @ts-ignore — thunks
		dispatch(fetchCriteria(evalId))
		// @ts-ignore — thunks
		dispatch(fetchSites(evalId))
	}, [dispatch, evalId])

	// Re-fetch ranking whenever criteria are populated. The summary is
	// meaningless without them.
	useEffect(() => {
		if (criteriaCount > 0) {
			// @ts-ignore — thunks
			dispatch(fetchScoringSummary(evalId))
		}
	}, [dispatch, evalId, criteriaCount])

	const handleAddClick = useCallback(() => {
		setEditingCriterion(null)
		setFormOpen(true)
	}, [])

	const handleEditClick = useCallback((criterion: any) => {
		setEditingCriterion(criterion)
		setFormOpen(true)
	}, [])

	const handleApplyTemplateClick = useCallback(() => {
		setApplyOpen(true)
	}, [])

	const handleSaveAsTemplateClick = useCallback(() => {
		setSaveAsOpen(true)
	}, [])

	const handleTemplateApplied = useCallback(() => {
		// The modal already POSTed to apply-scoring-template; refresh derived
		// state so the workspace shows the seeded criteria + new rollup.
		// @ts-ignore — thunks
		dispatch(fetchCriteria(evalId))
		// @ts-ignore — thunks
		dispatch(fetchScoringSummary(evalId))
	}, [dispatch, evalId])

	const leader = summary?.sites?.find((s: any) => s.overallScore != null) ?? null

	return (
		<div className="space-y-4">
			{/* Header */}
			<div>
				<h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
					<BarChart3 className="w-5 h-5 text-[#0080fe]" />
					Scoring
				</h3>
				<p className="text-sm text-gray-500">
					Define weighted criteria, score each site (1–10), and review weighted totals and rankings.
				</p>
			</div>

			{/* Leader banner — kept above the workspace so it's visible on mobile
			    where the panes stack below the matrix. */}
			{leader && leader.overallScore != null && (
				<Card className="border-l-4 border-l-primary">
					<CardContent className="p-4 flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/10 text-primary">
							<Trophy className="w-5 h-5" />
						</div>
						<div className="flex-1 min-w-0">
							<div className="text-xs text-gray-500">Current leader</div>
							<div className="text-sm font-semibold text-gray-900 truncate">
								{leader.siteName} — {leader.overallScore}/10
							</div>
						</div>
					</CardContent>
				</Card>
			)}

			<div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
				<div className={MATRIX_SPAN}>
					<ScoreEntrySection evalId={evalId} />
				</div>
				{/* ScoreProfileCard returns null when nothing is scored. space-y-4
				    uses `> * + *`, which doesn't match a component that renders no
				    node, so no phantom gap appears under RankingCard. */}
				<div className={`${SIDEBAR_SPAN} space-y-4`}>
					<RankingCard evalId={evalId} maxVisible={RANKING_VISIBLE_ROWS} />
					<ScoreProfileCard evalId={evalId} />
				</div>
				<div className={CRITERIA_SPAN}>
					<CriteriaPanel
						evalId={evalId}
						onAddClick={handleAddClick}
						onEditClick={handleEditClick}
						onApplyTemplateClick={handleApplyTemplateClick}
						onSaveAsTemplateClick={handleSaveAsTemplateClick}
					/>
				</div>
			</div>

			<CriterionForm
				open={formOpen}
				onClose={() => setFormOpen(false)}
				evalId={evalId}
				criterion={editingCriterion}
				defaultSortOrder={criteriaCount}
			/>

			<ApplyTemplateModalCN
				open={applyOpen}
				onOpenChange={setApplyOpen}
				templateType="scoring"
				evalId={evalId}
				onApplied={handleTemplateApplied}
			/>

			<SaveAsTemplateModalCN
				open={saveAsOpen}
				onOpenChange={setSaveAsOpen}
				templateType="scoring"
				sourceItems={evalCriteria}
			/>
		</div>
	)
}

export default ScoringWorkspace
